import { SlidersHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useEventsFilterStore } from '@/stores/events-filter-store';
import {
  EVENT_CATEGORY_KEYS,
  EVENT_CATEGORY_LABELS,
  EVENT_FILTER_PRESETS,
  type EventCategory,
  type EventFilterPreset,
} from './helpers/panel-event-helpers';

type EnabledMap = Record<EventCategory, boolean>;

function getActivePreset(enabled: EnabledMap): EventFilterPreset | '' {
  for (const preset of EVENT_FILTER_PRESETS) {
    const categories = new Set(preset.categories);
    const matches = EVENT_CATEGORY_KEYS.every(
      (key) => enabled[key] === categories.has(key),
    );
    if (matches) return preset.key;
  }
  return '';
}

export function EventsFilterMenu() {
  const enabled = useEventsFilterStore((s) => s.enabled);
  const toggleCategory = useEventsFilterStore((s) => s.toggleCategory);
  const applyPreset = useEventsFilterStore((s) => s.applyPreset);

  const activePreset = getActivePreset(enabled);
  const isFiltered =
    EVENT_CATEGORY_KEYS.filter((key) => enabled[key]).length <
    EVENT_CATEGORY_KEYS.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          aria-label='Filter events'
          className={cn(
            'relative inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground cursor-pointer',
            isFiltered && 'text-primary',
          )}
        >
          <SlidersHorizontal className='size-4' />
          {isFiltered && (
            <span className='absolute -right-0.5 -top-0.5 size-1.5 rounded-full bg-primary' />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-56'>
        <DropdownMenuLabel>Presets</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={activePreset}
          onValueChange={(value) => applyPreset(value as EventFilterPreset)}
        >
          {EVENT_FILTER_PRESETS.map((preset) => (
            <DropdownMenuRadioItem key={preset.key} value={preset.key}>
              {preset.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Show event types</DropdownMenuLabel>
        {EVENT_CATEGORY_KEYS.map((key) => (
          <DropdownMenuCheckboxItem
            key={key}
            checked={enabled[key]}
            onCheckedChange={() => toggleCategory(key)}
            onSelect={(event) => event.preventDefault()}
          >
            {EVENT_CATEGORY_LABELS[key]}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
